import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw, TrendingUp, TrendingDown, Download, BarChart3 } from 'lucide-react';

import type { DashboardMetric } from './types';

type MetricsCardsProps = {
  metrics: DashboardMetric[];
  onRefresh: () => void;
  isRefreshing: boolean;
  onViewDetails?: (metric: DashboardMetric) => void;
  onExportMetric?: (metric: DashboardMetric) => void;
};

export const MetricsCards: React.FC<MetricsCardsProps> = ({
  metrics,
  onRefresh,
  isRefreshing,
  onViewDetails,
  onExportMetric,
}) => {
  const { t } = useTranslation("dashboard");
  const { toast } = useToast();

  const handleView = (metric: DashboardMetric) => {
    if (onViewDetails) {
      onViewDetails(metric);
    } else {
      toast({ title: metric.label, description: t('metrics.viewFallback.description') });
    }
  };

  const handleExport = (metric: DashboardMetric) => {
    if (onExportMetric) {
      onExportMetric(metric);
    } else {
      toast({ title: t('metrics.exportFallback.title'), description: t('metrics.exportFallback.description', { label: metric.label }) });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t("metrics.title")}</h2>
        <Button
          variant="outline"
          size="sm"
          className="border-gray-200/70 bg-gray-50/80 hover:bg-white dark:border-gray-700/60 dark:bg-gray-800/80 dark:hover:bg-gray-700"
          onClick={onRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {t('metrics.refresh')}
        </Button>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <Card
            key={metric.id}
            className="group relative overflow-hidden border border-gray-200/60 bg-gradient-to-br from-white via-gray-50/60 to-white shadow-lg shadow-gray-500/5 transition-all duration-500 hover:shadow-xl dark:border-gray-700/60 dark:from-gray-900 dark:via-gray-800/50 dark:to-gray-900 dark:shadow-black/20"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(0,0,0,0.02),transparent_60%)] dark:bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.03),transparent_60%)]" />
            <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                {metric.label}
              </CardTitle>
              {metric.icon ? (
                <div className="rounded-xl border border-gray-200/60 bg-gray-100/80 p-2.5 text-gray-600 transition-all group-hover:bg-gray-50 dark:border-gray-700/60 dark:bg-gray-800/80 dark:text-gray-200">
                  {metric.icon}
                </div>
              ) : null}
            </CardHeader>
            <CardContent className="relative space-y-4">
              <div className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">{metric.value}</div>
              <Badge
                variant="secondary"
                className={
                  metric.trend === 'up'
                    ? 'border-transparent bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : metric.trend === 'down'
                    ? 'border-transparent bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                    : 'border-transparent bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-300'
                }
              >
                {metric.trend === 'up' && <TrendingUp className="mr-1 h-3 w-3" />}
                {metric.trend === 'down' && <TrendingDown className="mr-1 h-3 w-3" />}
                {metric.changeLabel}
              </Badge>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{metric.description}</p>
              <div className="flex h-8 items-end gap-1 opacity-70 transition-opacity duration-500 group-hover:opacity-100">
                {metric.history.map((value, index) => (
                  <span
                    key={index}
                    className="flex-1 rounded-t-sm bg-gradient-to-t from-emerald-500/40 via-emerald-400/35 to-emerald-300/30 shadow-sm"
                    style={{ height: `${Math.max(10, value)}%` }}
                  />
                ))}
              </div>
              <div className="flex gap-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleView(metric)}
                >
                  <BarChart3 className="mr-1 h-3 w-3" />
                  {t('metrics.actions.chart')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleExport(metric)}
                >
                  <Download className="mr-1 h-3 w-3" />
                  {t('metrics.actions.export')}
                </Button>
              </div>
            </CardContent>
            <div className="pointer-events-none absolute inset-0 translate-x-[-100%] bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 group-hover:translate-x-[100%]" />
          </Card>
        ))}
      </div>
    </div>
  );
};

